// =========================================================
// Vojjin UTILS Utilities
// =========================================================
import {registerGlobal} from "./core.js";

/* global Logger */
class Utils {


    /**
     * Checks if a given value is numeric.
     *
     * This function determines whether the provided input can be parsed
     * as a finite number. It uses `parseFloat` to validate the numeric
     * nature of the input and checks its finiteness to exclude non-numeric
     * values like NaN or Infinity.
     *
     * @param {any} number - The value to be checked for numeric validity.
     * @returns {boolean} True if the input is numeric and finite, otherwise false.
     */
    static isNumeric = (number) => {
        return !isNaN(parseFloat(number)) && isFinite(number);
    }

    /**
     * Checks if a given value is of the boolean data type.
     *
     * Determines whether the provided input is a boolean (`true` or `false`).
     *
     * @param {*} anyValue - The value to be checked.
     * @returns {boolean} - Returns `true` if the input is a boolean, otherwise `false`.
     */
    static isBoolean = (anyValue) => {
        return typeof anyValue === "boolean";
    }

    /**
     * Checks whether the provided value is an array.
     *
     * This function determines if the given input is an array by using
     * the built-in `Array.isArray` method. It returns `true` if the input
     * is an array, otherwise it returns `false`.
     *
     * @param {any} anyValue - The value to be evaluated for array type.
     * @returns {boolean} - Returns `true` if the input is an array, otherwise `false`.
     */
    static isArray = (anyValue) => {
        return Array.isArray(anyValue);
    }

    /**
     * Determines whether a given value is of type "object".
     *
     * @param {*} anyValue - The value to be checked.
     * @returns {boolean} Returns `true` if the value is an object, otherwise `false`.
     */
    static isObject = (anyValue) => {
        return typeof anyValue === "object";
    }

    /**
     * Checks whether the provided value is considered empty.
     *
     * A value is deemed empty based on the following conditions:
     * - `null` or `undefined` are considered empty.
     * - Numeric values are considered empty if they are equal to zero.
     * - Boolean values are considered empty if they are `false`.
     * - Arrays are considered empty if their length is zero.
     * - Objects are considered empty if they have no own enumerable properties.
     * - Strings (or values convertible to strings) are considered empty if they
     *   are empty or contain only whitespace characters.
     *
     * @param {any} anyValue - The value to be checked for emptiness.
     * @returns {boolean} Returns `true` if the value is empty, otherwise `false`.
     */
    static isEmpty = (anyValue) => {
        if (anyValue === null || anyValue === undefined) return true;
        if (this.isNumeric(anyValue)) return parseFloat(anyValue) === 0;
        if (this.isBoolean(anyValue)) return anyValue === false;
        if (this.isArray(anyValue)) return anyValue.length === 0;
        if (this.isObject(anyValue)) return Object.keys(anyValue).length === 0;
        return this.trim(anyValue.toString()) === "";
    }

    /**
     * Removes whitespace from both ends of a string.
     *
     * @param {string} str - The string to be trimmed.
     * @returns {string} The trimmed string.
     */
    static trim = (str) => {
        return typeof str === 'string' ? str.trim() : "";
    }

    /**
     * Checks if a given value exists and is not empty.
     *
     * This function evaluates whether the provided value is not empty
     * by using the `isEmpty` method. If the value is not empty, it
     * returns `true`; otherwise, it returns `false`.
     *
     * @param {*} anyValue - The value to be checked for existence.
     * @returns {boolean} Returns `true` if the value is not empty; otherwise, `false`.
     */
    static exists = (anyValue) => {
        return !this.isEmpty(anyValue);
    }

    /**
     * Checks if a specified value exists within an array and returns its index if found.
     * If the value is not found, returns false.
     *
     * @param {Array} array - The array to search through.
     * @param {*} searchValue - The value to search for within the array.
     * @returns {(number|boolean)} The index of the found value, or false if not found.
     */
    static inArray = (array, searchValue) => {
        if (!(this.isArray(array))) return false;
        for (let i = 0; i < array.length; i++) {
            if (array[i] === searchValue) return i;
        }
        return false;
    }

    /**
     * Copies the provided text to the clipboard.
     *
     * This function uses the Clipboard API to write the specified text
     * to the system clipboard. If the operation fails, an error is logged.
     *
     * @param {string} text - The text content to be copied to the clipboard.
     */
    static copyToClipboard = (text) => {
        try {
            navigator.clipboard.writeText(text).then(r => {
            });
        } catch (err) {
            Logger.error("Failed to copy to clipboard: ", err);
        }
    }

    /**
     * Generates a random alphanumeric string of the specified length.
     *
     * @param {number} [length=12] - The desired length of the random string. Defaults to 12 if not specified.
     * @param {boolean} [includeSpecials=false] - Determines whether to include special characters in the generated string. Defaults to false.
     * @returns {string} A randomly generated string containing alphanumeric characters, and optionally special characters if enabled.
     */
    static randomString = (length = 12, includeSpecials = false) => {
        let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        if (includeSpecials) characters += '!@#$%^&*()_+=-[]{}|;:,./<>?';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
    }

    /**
     * Removes all HTML tags from a given string and returns the plain text content.
     *
     * This function creates a temporary DOM element, sets its innerHTML property
     * to the provided input, and retrieves only the textual content, effectively
     * stripping out any HTML tags or markup.
     *
     * @param {string} html - The input string containing HTML markup to be stripped.
     * @returns {string} The plain text content with all HTML tags removed.
     */
    static stripTags = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    /**
     * Reads a file input as a Data URL and executes a callback with the file content and filename.
     *
     * @param {HTMLInputElement} input - The file input element containing the file to be read.
     * @param {function} callback - A callback function that is executed with two arguments:
     *                              1. The Data URL of the file content.
     *                              2. The name of the uploaded file.
     */
    static readURL = (input, callback) => {
        if (input.files && input.files[0]) {
            let uplfil = input.files[0].name;
            let reader = new FileReader();
            reader.onload = function (e) {
                let readedImage = e.target.result;
                if (readedImage !== "") {
                    callback(readedImage, uplfil);
                }
            };
            reader.readAsDataURL(input.files[input.files.length - 1]);
        }
    }

    /**
     * Formats a numeric jackpot amount into a localized currency string.
     *
     * The function uses the `Intl.NumberFormat` object to format the
     * given jackpot amount as per the current locale and currency settings.
     * The currency symbol is displayed in a narrow symbolic format, and
     * no fractional digits are included in the formatted output.
     *
     * @param {number} jackpotAmount - The numeric jackpot amount to format.
     * @returns {string} The formatted currency string representing the jackpot amount.
     */
    static formatCurrency = (jackpotAmount) => {
        let currencyFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: window.currency ?? 'USD',
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        return currencyFormatter.format(jackpotAmount);
    }

    /**
     * Replaces all occurrences of a specified substring or pattern within a string with a given replacement string.
     *
     * @param {string} str - The input string in which replacements are to be made.
     * @param {string|array} find - The substring or pattern to be replaced. Can be a string or a regular expression pattern.
     * @param {string|array} replace - The string to replace each occurrence of the specified substring or pattern.
     * @returns {string} A new string with all occurrences of the specified substring or pattern replaced by the replacement string.
     */
    static replaceAll = (str, find, replace) => {
        if (typeof find === 'string') {
            return str.replace(new RegExp(find, 'g'), replace);
        }
        let out = str;
        for (let i = 0; i < find.length; i++) {
            out = out.replace(new RegExp(find[i], 'g'), replace[i]);
        }
        return out;
    }

    static globalListener = function (what, selector, handler) {
        document.addEventListener(what, function (e) {
            const targetEl = $$(e.target).closest(selector);
            if (!targetEl) return;
            handler(targetEl, e);
        });
    }

    static globalListenerAnySelector = function (what, handler) {
        document.addEventListener(what, function (e) {
            handler(e);
        });
    }

    static globalListenerWindow = function (eventName, handler) {
        window.addEventListener(eventName, (e) => {
            handler(e);
        });
    };


}

registerGlobal("Utils", Utils);
